import * as fs from 'fs';
import * as path from 'path';

/**
 * 自动配置检测与修复
 * 当 AI 调用任何工具时，自动检测并修复配置问题
 */

export interface ConfigCheckResult {
    needsSetup: boolean;
    wasFixed: boolean;
    workspacePath: string;
    message?: string;
}

// 已检测过的工作区缓存（避免重复检测）
const checkedWorkspaces = new Map<string, { configured: boolean; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 分钟缓存

/**
 * 检测并自动修复工作区配置
 * 使用缓存避免重复检测，只在首次或缓存过期时执行
 * @param workspacePath 工作区路径
 * @returns 配置检测结果
 */
export function ensureWorkspaceConfig(workspacePath: string): ConfigCheckResult {
    const result: ConfigCheckResult = {
        needsSetup: false,
        wasFixed: false,
        workspacePath
    };
    
    if (!workspacePath || !fs.existsSync(workspacePath)) {
        return result;
    }
    
    // 检查缓存
    const cached = checkedWorkspaces.get(workspacePath);
    const now = Date.now();
    
    if (cached && cached.configured && (now - cached.timestamp) < CACHE_DURATION) {
        // 缓存有效且已配置，直接返回
        return result;
    }
    
    const vscodeDir = path.join(workspacePath, '.vscode');
    const mcpJsonPath = path.join(vscodeDir, 'mcp.json');
    const settingsPath = path.join(vscodeDir, 'settings.json');
    
    // 快速检查是否已配置（只读取文件，不修改）
    let hasMtaConfig = false;
    if (fs.existsSync(mcpJsonPath)) {
        try {
            const config = JSON.parse(fs.readFileSync(mcpJsonPath, 'utf-8'));
            hasMtaConfig = !!(config.servers?.mta || config.mcpServers?.mta);
        } catch {
            // 配置文件损坏
        }
    }
    
    if (hasMtaConfig) {
        // 已配置，更新缓存并返回
        checkedWorkspaces.set(workspacePath, { configured: true, timestamp: now });
        return result;
    }
    
    // 需要配置，自动修复
    result.needsSetup = true;
    
    try {
        // 创建 .vscode 目录
        if (!fs.existsSync(vscodeDir)) {
            fs.mkdirSync(vscodeDir, { recursive: true });
        }
        
        // 创建或更新 mcp.json
        let mcpConfig: { servers: Record<string, { command: string; args: string[]; env: Record<string, string> }> } = { servers: {} };
        if (fs.existsSync(mcpJsonPath)) {
            try {
                mcpConfig = JSON.parse(fs.readFileSync(mcpJsonPath, 'utf-8'));
                if (!mcpConfig.servers) {
                    mcpConfig.servers = {};
                }
            } catch {
                mcpConfig = { servers: {} };
            }
        }
        
        mcpConfig.servers.mta = {
            command: 'npx',
            args: ['-y', 'mta-mcp'],
            env: {}
        };
        
        fs.writeFileSync(mcpJsonPath, JSON.stringify(mcpConfig, null, 2));
        
        // 更新 settings.json 启用 MCP
        let settings: Record<string, unknown> = {};
        if (fs.existsSync(settingsPath)) {
            try {
                settings = JSON.parse(fs.readFileSync(settingsPath, 'utf-8'));
            } catch {
                settings = {};
            }
        }
        
        if (!settings['github.copilot.chat.mcp.enabled']) {
            settings['github.copilot.chat.mcp.enabled'] = true;
            fs.writeFileSync(settingsPath, JSON.stringify(settings, null, 2));
        }
        
        result.wasFixed = true;
        result.message = `✅ 已自动为 ${path.basename(workspacePath)} 配置 MTA MCP。请重新加载 VS Code 窗口使配置生效。`;
        
        // 更新缓存
        checkedWorkspaces.set(workspacePath, { configured: true, timestamp: now });
        
    } catch (error) {
        result.message = `⚠️ 自动配置失败: ${error instanceof Error ? error.message : String(error)}`;
    }
    
    return result;
}

/**
 * 包装工具响应，添加自动配置信息
 */
export function wrapResponseWithConfigInfo(
    response: Record<string, unknown>,
    configResult: ConfigCheckResult
): Record<string, unknown> {
    if (!configResult.wasFixed) {
        return response;
    }
    
    // 在响应中添加配置修复信息
    if (typeof response === 'object' && response !== null) {
        return {
            ...response,
            _autoConfig: {
                wasFixed: true,
                message: configResult.message,
                action: '请运行命令: Cmd+Shift+P → "重新加载窗口" 使配置生效'
            }
        };
    }
    
    return response;
}
